import React, { useEffect, useMemo, useState } from 'react'
import FeedCenterCard from './FeedCenterCard'
import { isNumber } from '@/utils/checks'
import FilterWizardSection from '@/components/organisms/FilterWizardSection/FilterWizardSection'
import { shouldRenderInsuranceMiniCarousel } from '@/domain/useCases/insurance/shouldRenderInsuranceMiniCarousel'
import { getLocationPartsTitles } from '../../domain/location/useCases/getLocationPartsTitles'
import { InsuranceFeedCarousel } from './InsuranceFeedCarousel'
import {
  CarouselType,
  FeedMode,
  FeedOptions,
  PageType,
  RelatedLocation,
  SearchState
} from '../../types/models'
import ShowMoreButton from './ShowMoreButton'
import NearbyFeedHeader from './NearbyFeedHeader'
import TopLocationsSection from './TopLocationsSection'

import { DEFAULT_RESULTS_PER_PAGE } from '@/domain/search/constants'

import AsyncCardLoader from './AsyncCardLoader'
import {
  isFacetPageType,
  isLocationPageType
} from '@/domain/useCases/pageTypeCheck'
import getLocationStringFromRefinementList from '@/domain/search/getLocationStringFromRefinementList'
import shouldUseNewUI from '@/domain/useCases/shouldUseNewUI'
import ExplorePopularDestinations from '@/components/organisms/ExplorePopularDestinations/ExplorePopularDestinations'
import LocationFeaturedCentersCarousel from '@/components/browse/LocationFeaturedCentersCarousel'
import { RefinementList } from '@/domain/search/types'
import { PrimaryFocus } from '@/domain/primaryFocus/constant'
import { getSessionStorageSearchState } from '@/utils/search/searchState'
import { navigateToBrowsePageWithQueryParams } from '@/domain/search/navigateToBrowsePageWithQueryParams'
import { getTitle } from '@/domain/location'
import normalizeSearchState from '@/domain/search/normalizeSearchState'
import { Department } from '@/domain/department/department'

type CenterFeedRenderProps = {
  feed
  queryID: string
  pageTitle?: string
  activeFacets?: string[]
  shouldDefaultRegionalize: boolean
  title: string
  pageContext?
  pageData?
  searchState?: SearchState
  customTermHeader?: string
  pageType: PageType
  locationType?: string
  nationalFeaturedCenters?
  termData?
  featuredCentersLoading: boolean
  nearbyCentersLoading?: boolean
  browseCTA?: string
  currentRefinements?
  termFocus?: PrimaryFocus
  refinementLocation?: string | number
  sortedLocations?: RelatedLocation[]
  department: Department
}

// TODO move this to some constant file
const MAX_FACET_PAGE_HIT_COUNT = 100
const MODIFIED_DEFAULT_CENTERS_TO_DISPLAY = 26
const CenterFeedRender = ({
  feed,
  queryID,
  pageTitle,
  activeFacets,
  shouldDefaultRegionalize,
  title,
  pageContext,
  pageData,
  searchState,
  customTermHeader,
  pageType = '' as PageType,
  locationType,
  nationalFeaturedCenters,
  termData,
  featuredCentersLoading,
  nearbyCentersLoading,
  browseCTA,
  currentRefinements,
  termFocus,
  refinementLocation,
  sortedLocations,
  department
}: CenterFeedRenderProps): JSX.Element => {
  const [countCentersToRender, setCountCentersToRender] = useState<number>(
    MODIFIED_DEFAULT_CENTERS_TO_DISPLAY
  )

  const isFacetOrLocation =
    isFacetPageType(pageType) ||
    isLocationPageType(pageType) ||
    countCentersToRender >= MAX_FACET_PAGE_HIT_COUNT

  const itShouldRedirectToBrowse =
    isFacetOrLocation && countCentersToRender >= feed.length

  const feedHasMoreCentersToShow = countCentersToRender < feed.length
  const showLoadMore = itShouldRedirectToBrowse || feedHasMoreCentersToShow

  const onLoadMoreClick = () => {
    if (
      feed.length <= DEFAULT_RESULTS_PER_PAGE ||
      countCentersToRender >= feed.length
    ) {
      const searchState = getSessionStorageSearchState()

      const locationName =
        isLocationPageType(pageType) || isFacetPageType(pageType)
          ? searchState.locationName
          : getTitle(pageData?.location)

      const locationsRefinement =
        isLocationPageType(pageType) || isFacetPageType(pageType)
          ? []
          : searchState.refinementList?.locations

      navigateToBrowsePageWithQueryParams(
        normalizeSearchState({
          ...searchState,
          locationName,
          refinementList: {
            ...searchState.refinementList,
            locations: locationsRefinement
          }
        })
      )
    }

    if (countCentersToRender < feed.length) {
      setCountCentersToRender(countCentersToRender + DEFAULT_RESULTS_PER_PAGE)
    }
  }

  const shouldRenderInsuranceSection = shouldRenderInsuranceMiniCarousel({
    locationParts: getLocationPartsTitles(pageData?.location),
    pageType
  })

  const shouldRenderTopLocationsSection =
    shouldUseNewUI(pageType) &&
    !isFacetPageType(pageType) &&
    sortedLocations &&
    sortedLocations.length > 3

  let nonCountedIndex = 0
  useEffect(() => {
    if (isNumber(nonCountedIndex) && nonCountedIndex > 0) {
      setCountCentersToRender(countCentersToRender + 1)
    }
    return () => {
      setCountCentersToRender(MODIFIED_DEFAULT_CENTERS_TO_DISPLAY)
    }
  }, [nonCountedIndex])

  const renderedFeed = useMemo(() => {
    return feed
      .map((item, index) => {
        if (item.feedType === FeedOptions.Local) {
          return (
            <div
              key={`${item.feedType}-${index}`}
              className={`p-l ${item.slug} ${item.feedType}`}
            >
              <FeedCenterCard
                center={item}
                index={item.feedPosition}
                pageTitle={pageTitle || 'browse'}
                cardIndex={item.feedPosition - nonCountedIndex}
                activeFacets={activeFacets || []}
                queryID={queryID}
                isAd={false}
                isNearbyFeed={false}
                zoneId={item.zoneId}
                locationType={locationType || ''}
                currentRefinements={currentRefinements}
                termFocus={termFocus || ''}
                refinementLocation={refinementLocation || ''}
                pageType={pageType}
                department={department}
                termFeedMode={termData.feedMode}
              />
            </div>
          )
        }
        if (item.feedType === FeedOptions.FilterWizard) {
          nonCountedIndex = nonCountedIndex + 1
          return (
            <div
              key={`${item.feedType}-${index}`}
              className="mt-6 sm:mt-8"
            >
              <FilterWizardSection isSection={false} />
            </div>
          )
        }
        if (item.feedType === FeedOptions.MidFeed) {
          return (
            <div
              key={`${item.feedType}-${index}`}
              className={`p-m m-${item?.placedInFeed} ${item.slug} ${item.feedType} `}
            >
              <FeedCenterCard
                center={item}
                index={item.feedPosition}
                pageTitle={title || 'browse'}
                cardIndex={item.feedPosition - nonCountedIndex}
                activeFacets={activeFacets || []}
                queryID={queryID}
                isNearbyFeed={item.feedType === FeedOptions.Nearby}
                zoneId={item.zoneId}
                isAd={true}
                locationType={locationType || ''}
                currentRefinements={currentRefinements}
                termFocus={termFocus || ''}
                pageType={pageType}
                department={department}
                termFeedMode={termData.feedMode}
              />
            </div>
          )
        }
        if (item.feedType === FeedOptions.NearbyHeader) {
          nonCountedIndex = nonCountedIndex + 1
          return (
            <NearbyFeedHeader
              key={`${item.feedType}`}
              title={title}
              pageType={pageType}
              termData={termData}
              customTermHeader={customTermHeader}
              shouldDefaultRegionalize={shouldDefaultRegionalize}
            />
          )
        }
        if (item.feedType === FeedOptions.Organic) {
          return (
            <div
              key={`${item.slug}-${index}`}
              className={`p-o ${item.slug} ${item.feedType}`}
            >
              <FeedCenterCard
                center={item}
                index={item.feedPosition}
                pageTitle={pageTitle || 'browse'}
                cardIndex={item.feedPosition - nonCountedIndex}
                activeFacets={activeFacets || []}
                queryID={queryID}
                isNearbyFeed={item.feedType === FeedOptions.Nearby}
                zoneId={item.zoneId}
                isAd={item.feedType === FeedOptions.MidFeed}
                locationType={locationType || ''}
                currentRefinements={currentRefinements}
                termFocus={termFocus || ''}
                pageType={pageType}
                department={department}
                termFeedMode={termData.feedMode}
              />
            </div>
          )
        }

        if (item.feedType === FeedOptions.Nearby) {
          return (
            <AsyncCardLoader
              key={`${item.slug}-${index}`}
              slug={item.slug}
              isLoading={nearbyCentersLoading || false}
            >
              <FeedCenterCard
                center={item}
                index={item.feedPosition}
                pageTitle={getLocationStringFromRefinementList(
                  currentRefinements as RefinementList
                )}
                cardIndex={item.feedPosition - nonCountedIndex}
                activeFacets={activeFacets || []}
                queryID={queryID}
                isAd={item.feedType === FeedOptions.MidFeed}
                isNearbyFeed={item?.feedType === FeedOptions.Nearby}
                zoneId={item.zoneId}
                locationType={locationType || ''}
                currentRefinements={currentRefinements}
                termFocus={termFocus || ''}
                pageType={pageType}
                department={department}
                termFeedMode={termData.feedMode}
              />
            </AsyncCardLoader>
          )
        }
        if (
          item.feedType === FeedOptions.Carousel &&
          item.itemType === CarouselType.Insurance
        ) {
          if (!shouldRenderInsuranceSection) {
            nonCountedIndex = nonCountedIndex + 1
            return null
          }

          return (
            <div key={item.feedPosition}>
              <InsuranceFeedCarousel
                shouldRenderInsuranceSection={shouldRenderInsuranceSection}
                pageType={pageType}
                pageData={pageData}
                searchState={searchState}
                termData={termData}
                primaryFocus={termFocus}
                feedPosition={item.feedPosition}
                titleOverride={title}
              />
            </div>
          )
        }

        if (
          item.feedType === FeedOptions.Carousel &&
          item.itemType === CarouselType.NationWide
        ) {
          if (!isFacetOrLocation) {
            nonCountedIndex = nonCountedIndex + 1
            return null
          }
          return (
            <LocationFeaturedCentersCarousel
              key={item.feedPosition}
              pageType={pageType}
              nationalFeaturedCenters={nationalFeaturedCenters}
              featuredCentersLoading={featuredCentersLoading}
              feedPosition={item.feedPosition}
              title={title}
              termData={termData}
            />
          )
        }
        if (
          item.feedType === FeedOptions.Carousel &&
          item.itemType === CarouselType.TopLocations &&
          shouldRenderTopLocationsSection
        ) {
          return (
            <TopLocationsSection
              key={item.feedPosition}
              pageType={pageType}
              relatedLocations={sortedLocations}
              global={pageContext?.global}
              inFeed={true}
              term={termData}
            />
          )
        }

        if (
          item.feedType === FeedOptions.Explore &&
          isLocationPageType(pageType)
        ) {
          return (
            <ExplorePopularDestinations
              location={pageData?.location}
              key="explore"
              searchState={searchState}
              pageType={pageType}
              feedPosition={item.feedPosition}
              refinements={currentRefinements}
            />
          )
        }
        return null
      })
      .filter(Boolean)
  }, [feed, featuredCentersLoading])

  return (
    <div className="feed-items">
      {renderedFeed.slice(0, countCentersToRender)}
      {showLoadMore && (
        <div className="relative mt-12 text-center">
          <ShowMoreButton
            onClick={onLoadMoreClick}
            browseLink={browseCTA}
          />
        </div>
      )}
    </div>
  )
}

export default CenterFeedRender
